import DataTable from "@/components/data-table";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import Spinner from "@/components/Spinner";
import { Card, CardContent } from "@/components/ui/card";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { List } from "@/components/list/List";

import { DataTableSettingsContextProvider } from "@/components/data-table/DataTableSettingsContext";
import { SuccessFetchListResponse } from "@/services/api";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { PATH_PROJECT_RATE_NEW } from "@/constants";
import { useNavigate, generatePath } from "react-router-dom";
import { ReadRoleBasedProjectRates } from "@/schemas/roleBasedProjectRates";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCheckIsProjectLocked } from "../../hooks/useCheckIsProjectLocked";

export const ProjectRateListContent = () => {
  const tableRef = useRef<HTMLDivElement>(null);
  const projectId = useProjectId();
  const { t } = useTranslation();
  const { query } =
    useDataTableQuery<SuccessFetchListResponse<ReadRoleBasedProjectRates>>();
  const settings = useDataTableSettings<ReadRoleBasedProjectRates>();
  const { can_update } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();
  const navigate = useNavigate();

  const onAddRate = useCallback(() => {
    navigate(
      generatePath(PATH_PROJECT_RATE_NEW, {
        projectId,
      }),
    );
  }, [navigate, projectId]);

  return (
    <Card>
      <CardContent className="gap-2 p-2 justify-between relative grow">
        <div className="flex flex-col gap-2 border min-h-32 h-full">
          {query.isLoading ? (
            <Spinner
              className="h-full w-full grow"
              label={t("table.loading")}
            />
          ) : (
            <>
              <DataTableSettingsContextProvider value={settings}>
                <DataTable ref={tableRef} />
                <List.Status className="py-3 px-2 ">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={onAddRate}
                    disabled={!can_update || isProjectLocked}
                  >
                    <PlusIcon className="w-4 h-4" />
                    {t("projectRates.buttons.add")}
                  </Button>
                </List.Status>
              </DataTableSettingsContextProvider>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
